import { isPurchaseCreditsRow } from './ledgerUtils';
function str(v) {
    return v != null ? String(v).trim() : '';
}
function parseMeta(row) {
    return row.metadata && typeof row.metadata === 'object' ? row.metadata : {};
}
function resolveWalletOwnerType(row) {
    var _a, _b, _c;
    const rec = row;
    const wallet = ((_a = rec.usdWallet) !== null && _a !== void 0 ? _a : rec.usd_wallet);
    const fromWallet = str((_b = wallet === null || wallet === void 0 ? void 0 : wallet.ownerType) !== null && _b !== void 0 ? _b : wallet === null || wallet === void 0 ? void 0 : wallet.owner_type).toLowerCase();
    if (fromWallet)
        return fromWallet;
    const type = str(row.type).toLowerCase();
    const party = type === 'withdrawal' || type === 'purchase_credits'
        ? row.fromParty
        : type === 'deposit'
            ? row.toParty
            : (_c = row.fromParty) !== null && _c !== void 0 ? _c : row.toParty;
    return str(party === null || party === void 0 ? void 0 : party.ownerType).toLowerCase();
}
function rolePrefix(ownerType) {
    switch (ownerType) {
        case 'shop':
            return 'Shop';
        case 'agent':
            return 'Agent';
        case 'super_agent':
            return 'Super agent';
        case 'player':
            return 'Player';
        case 'platform':
            return 'Platform';
        default:
            return ownerType ? ownerType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Account';
    }
}
function roleNoun(ownerType) {
    return rolePrefix(ownerType).toLowerCase();
}
function activity(role, action) {
    return `${role} - ${action}`;
}
/** Shop SMS/email campaign billed to the shop USD wallet (default $0.05/SMS). */
export function isMessagingFeeRow(row) {
    var _a, _b;
    const ref = str((_a = row.referenceType) !== null && _a !== void 0 ? _a : row.reference_type).toLowerCase();
    if (ref === 'messaging_campaign')
        return true;
    const txnId = str((_b = row.transactionId) !== null && _b !== void 0 ? _b : row.transaction_id).toLowerCase();
    if (txnId.startsWith('messaging_fee:'))
        return true;
    const meta = parseMeta(row);
    if (str(meta.source).toLowerCase() === 'shop_messaging')
        return true;
    if (str(meta.category).toLowerCase().includes('messaging fee'))
        return true;
    return false;
}
/** Player deposit credited to a tier wallet (player has no USD wallet). */
export function isPlayerAttributedDeposit(row) {
    var _a, _b;
    const type = str(row.type).toLowerCase();
    if (type !== 'deposit')
        return false;
    const meta = parseMeta(row);
    if (str((_a = meta.playerId) !== null && _a !== void 0 ? _a : meta.player_id))
        return true;
    const txnId = str((_b = row.transactionId) !== null && _b !== void 0 ? _b : row.transaction_id);
    if (/:player:/i.test(txnId))
        return true;
    const source = str(meta.source).toLowerCase();
    if (source.includes('creditplayermainwallet'))
        return true;
    const note = str(meta.note).toLowerCase();
    if (note.includes('player has no usd_wallet'))
        return true;
    return false;
}
/** Tier wallet debited to pay out a player withdrawal. */
export function isPlayerPayoutWithdrawal(row) {
    var _a, _b, _c;
    const type = str(row.type).toLowerCase();
    if (type !== 'withdrawal')
        return false;
    const txnId = str((_a = row.transactionId) !== null && _a !== void 0 ? _a : row.transaction_id);
    if (txnId.includes('player_payout'))
        return true;
    const meta = parseMeta(row);
    if (str(meta.source) === 'playerPayoutTierSettlementBizService')
        return true;
    const ref = str((_b = row.referenceType) !== null && _b !== void 0 ? _b : row.reference_type).toLowerCase();
    if (str((_c = meta.playerId) !== null && _c !== void 0 ? _c : meta.player_id) && ref === 'payment_transaction')
        return true;
    return false;
}
/** Portal online-balance withdrawal requested by shop/agent/super agent. */
export function isPortalTierWithdrawal(row) {
    var _a, _b;
    const type = str(row.type).toLowerCase();
    if (type !== 'withdrawal')
        return false;
    const ref = str((_a = row.referenceType) !== null && _a !== void 0 ? _a : row.reference_type).toLowerCase();
    if (ref === 'usd_withdrawal_request')
        return true;
    const txnId = str((_b = row.transactionId) !== null && _b !== void 0 ? _b : row.transaction_id);
    if (txnId.startsWith('usd_withdrawal_request:'))
        return true;
    const createdBy = str(parseMeta(row).createdBy).toLowerCase();
    if (createdBy.endsWith('_portal'))
        return true;
    return false;
}
/**
 * Human-readable activity for USD ledger rows (who initiated / what happened).
 */
export function usdLedgerActivityLabel(row) {
    var _a, _b;
    const ownerType = resolveWalletOwnerType(row);
    const role = rolePrefix(ownerType);
    const type = str(row.type).toLowerCase();
    if (isMessagingFeeRow(row)) {
        if (type === 'deposit')
            return activity(role, 'receive message fee');
        return activity(role, 'pay message fee');
    }
    if (isPurchaseCreditsRow(row)) {
        return activity(role, 'pay for converting balance to credits');
    }
    const refType = str((_a = row.referenceType) !== null && _a !== void 0 ? _a : row.reference_type).toLowerCase();
    if (refType === 'balance_to_credits_settlement') {
        const meta = parseMeta(row);
        const counterpart = str((_b = meta.counterpart_owner_type) !== null && _b !== void 0 ? _b : meta.counterpartOwnerType).toLowerCase();
        const fromNoun = counterpart ? roleNoun(counterpart) : 'buyer';
        return activity(role, `receive usd after ${fromNoun} convert balance to credits`);
    }
    if (type === 'deposit') {
        if (isPlayerAttributedDeposit(row))
            return activity(role, 'receive player deposit');
        return activity(role, 'receive deposit');
    }
    if (type === 'withdrawal') {
        if (isPlayerPayoutWithdrawal(row))
            return activity(role, 'pay player withdrawal');
        if (isPortalTierWithdrawal(row))
            return activity(role, 'withdraw usd');
        return activity(role, 'pay withdrawal');
    }
    const displayLabel = str(row.displayLabel);
    if (displayLabel)
        return displayLabel;
    if (type === 'adjustment') {
        return activity(role, 'balance correction');
    }
    if (type === 'reversal') {
        return activity(role, 'reversal');
    }
    return '—';
}
